/**
 * The bell (AIC-511): in-app notifications that it's the analyst's move. In-app only: nothing is ever emailed.
 *
 * - The bell sits in the Platform bar, before the avatar (or in the SDK bar's actions slot). Its unread count is
 *   GET /me/counts' `notifications`, the portal's existing light poll; no other request runs until the bell is pressed.
 *   No count (null: analyst preview, practice, or a server without migration 0058) means no bell.
 * - Pressing it opens a look-up drawer (./Drawer.tsx, the AIC-503 standard: 440px, overlays, Esc / Back / scrim close,
 *   focus to the heading and back to the bell). It lists "Since you last looked" (the unread ones) and "Earlier" (the
 *   last fourteen days, read), newest first. Each item links to its assignment.
 * - Opening marks them read: once the list has loaded, the drawer tells the server it showed everything up to the newest
 *   item (seen_through), so the badge goes to zero. The items stay marked new in the open drawer.
 * - What anyone sees follows RLS on the server: an analyst their own assignments, a manager their chain.
 */
import { useEffect, useRef, useState } from "react";
import { usePortal } from "../api/hooks.ts";
import type { NotificationItem, NotificationList } from "../api/schemas.ts";
import { Link } from "../router.tsx";
import { ErrorNote } from "./common.tsx";
import { Drawer } from "./Drawer.tsx";
import { LineIcon } from "./LineIcon.tsx";
import "./Notifications.css";

const NOTIFICATIONS_DRAWER_ID = "notifications";

/** "99+" past 99, so the badge keeps its size. */
export const badgeText = (n: number): string => (n > 99 ? "99+" : String(n));

/** "Just now", "12 min ago", "3 h ago", "Yesterday", else "3 Oct": short, for a list scanned at a glance. */
export function whenText(iso: string, now = new Date()): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const min = Math.floor((now.getTime() - t) / 60_000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min} min ago`;
  // Hours on the same day, and for the first few hours either way (90 minutes after midnight is not "Yesterday").
  if (min < 6 * 60 || (min < 24 * 60 && new Date(t).toDateString() === now.toDateString())) return `${Math.floor(min / 60)} h ago`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (new Date(t).toDateString() === yesterday.toDateString()) return "Yesterday";
  return new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** The kinds that need a decision or a fix, in the alert tone; the rest are progress. */
const ALERT_KINDS = new Set(["export_failed", "analyst_requested"]);

/**
 * The bar's bell. The drawer it opens is the Shell's (rendered in the app, not in the bar, which may be the SDK's
 * element), so the bell only says whether it is open.
 */
export function NotificationBell({ count, open, onOpen }: { readonly count: number; readonly open: boolean; readonly onOpen: () => void }) {
  const label = count > 0 ? `Notifications, ${count > 99 ? "more than 99" : count} new` : "Notifications, nothing new";
  return (
    <button
      type="button" className={`ipd-bar-bell${count > 0 ? " has-unread" : ""}`} aria-label={label} title="Notifications"
      aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? NOTIFICATIONS_DRAWER_ID : undefined} onClick={onOpen}
    >
      <LineIcon name="bell" />
      {count > 0 ? <span className="ipd-bar-bell-count" aria-hidden="true">{badgeText(count)}</span> : null}
    </button>
  );
}

type State = { state: "loading" } | { state: "error"; error: Error } | { state: "ready"; data: NotificationList };

/** `onSeen`: the server took the mark, so the bell can show nothing new before the next counts read says so. */
export function NotificationsDrawer({ onClose, onSeen }: { readonly onClose: () => void; readonly onSeen?: () => void }) {
  const { api } = usePortal();
  const [load, setLoad] = useState<State>({ state: "loading" });
  const [tick, setTick] = useState(0);
  const onSeenRef = useRef(onSeen);
  onSeenRef.current = onSeen;
  useEffect(() => {
    let live = true;
    setLoad((l) => (l.state === "ready" ? l : { state: "loading" }));
    api.notifications().then(
      (data) => {
        if (!live) return;
        setLoad({ state: "ready", data });
        // Opening marks them read, up to the newest one shown (never past what the drawer was given).
        if (data.unread > 0 && data.seen_through) void api.markNotificationsSeen(data.seen_through).then(() => { if (live) onSeenRef.current?.(); }, () => {});
      },
      (error: unknown) => { if (live) setLoad({ state: "error", error: error instanceof Error ? error : new Error(String(error)) }); },
    );
    return () => { live = false; };
  }, [api, tick]);

  const fresh = load.state === "ready" ? load.data.items.filter((i) => i.unread) : [];
  const earlier = load.state === "ready" ? load.data.items.filter((i) => !i.unread) : [];
  const sub = load.state !== "ready" ? "Your assignments" : fresh.length ? `${fresh.length} new since you last looked` : "Nothing new since you last looked";
  return (
    <Drawer
      id={NOTIFICATIONS_DRAWER_ID} headingId="notifications-h" title="Notifications" sub={sub} closeLabel="Close notifications" onClose={onClose}
      bodyClassName="ipd-notes-body" footer={<span>In-app only. Nothing is emailed. Showing the last 14 days.</span>}
    >
      {load.state === "loading" ? <p className="ipd-notes-status" role="status">Loading notifications…</p> : null}
      {load.state === "error" ? <ErrorNote error={load.error} onRetry={() => setTick((n) => n + 1)} /> : null}
      {load.state === "ready" ? <>
        <NoteGroup id="notes-new" title="Since you last looked" items={fresh} onOpen={onClose} empty="You're up to date. New activity on your assignments shows here." />
        {earlier.length ? <NoteGroup id="notes-earlier" title="Earlier" items={earlier} onOpen={onClose} /> : null}
      </> : null}
    </Drawer>
  );
}

function NoteGroup({ id, title, items, empty, onOpen }: { readonly id: string; readonly title: string; readonly items: readonly NotificationItem[]; readonly empty?: string; readonly onOpen: () => void }) {
  return (
    <section className="ipd-notes-group" aria-labelledby={`${id}-h`}>
      <h3 id={`${id}-h`} className="ipd-notes-label">{title}</h3>
      {items.length === 0 ? <p className="ipd-notes-empty">{empty}</p> : (
        <ul className="ipd-notes-list">
          {items.map((n) => {
            // AIC-815: the outcome of a "Report a mismatch" has no assignment: it goes to My profile and says what the admin did.
            const profile = n.assignment === null;
            const title = profile ? n.profile?.reported ?? "Your report" : n.assignment!.company;
            const note = profile && n.profile?.admin_note ? `. ${n.profile.admin_note}` : "";
            return (
              <li key={n.id}>
                <Link to={profile ? "/profile" : `/assignments/${encodeURIComponent(n.assignment!.id)}`} className={`ipd-note${n.unread ? " is-new" : ""}${ALERT_KINDS.has(n.kind) ? " is-alert" : ""}`} data-kind={n.kind} onClick={onOpen}
                  aria-label={`${title}: ${n.label}${note}${n.unread ? ", new" : ""}, ${whenText(n.at)}`}>
                  <span className="ipd-note-dot" aria-hidden="true" />
                  <span className="ipd-note-text">
                    <b>{title}</b>
                    <span>{n.label}</span>
                    {profile && n.profile?.admin_note ? <span>{n.profile.admin_note}</span> : null}
                  </span>
                  <time dateTime={n.at} title={new Date(n.at).toLocaleString()}>{whenText(n.at)}</time>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
